import { type FunctionComponent, useCallback } from 'react';
import styled from '@emotion/styled';
import { EditOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, Col, Row, Space } from 'antd';
import { useApolloClient, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AuthGuard } from '../../../../../components/AuthGuard';
import { DeleteButton } from '../../../../../components/DeleteButton';
import { BreakableTitle } from '../../../../../components/BreakableTitle';
import { UserRequiredInfo } from '../../../../../components/UserRequiredInfo';
import {
  DELETE_RECIPE,
  type IContext as IDeleteRecipeContext,
  type IVariables as IDeleteRecipeVariables,
} from '../../../../../gql/delete-recipe';
import type { IRecipe } from '../../../../../gql/get-recipe';
import { handleError } from '../../../../../lib/handleError';
import {
  defaultActionButtonMargin,
  defaultGutterPixelSize,
} from '../../../../../lib/styles';
import { roles } from '../../../../../lib/userRoles';
import { PrintButton } from './Buttons/PrintButton';
import { MailButton } from './Buttons/MailButton';
import { CopyButton } from './Buttons/CopyButton';
import { EditButton } from './Buttons/EditButton';
import { GET_USER } from '../../../../../gql/get-user';
import { TransferRecipeModal } from '../../../../../components/TransferRecipeModal';

const ActionCol = styled(Col)`
  ${defaultActionButtonMargin};
`;

interface IProps {
  recipe: IRecipe;
}

export const RecipeTitle: FunctionComponent<IProps> = ({ recipe }) => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data } = useQuery(GET_USER);

  const handleConfirmDelete = useCallback(async () => {
    try {
      const response = await client.mutate<
        IDeleteRecipeContext,
        IDeleteRecipeVariables
      >({
        mutation: DELETE_RECIPE,
        variables: {
          input: {
            id: recipe.id,
          },
        },
      });

      if (!response.errors) {
        navigate(recipe.isSAP ? '/manage-recipes' : '/recipes/own');
      }
    } catch (e) {
      handleError(e);
    }
  }, [client, navigate, recipe.id, recipe.isSAP]);

  return (
    <Row gutter={defaultGutterPixelSize} justify="space-between">
      <Col style={{ maxWidth: '100%' }}>
        <BreakableTitle>{recipe.title}</BreakableTitle>
      </Col>
      <ActionCol>
        <AuthGuard>
          {(isGranted) =>
            isGranted ? (
              <Space wrap>
                {(recipe.user !== null || recipe.isSAP) && (
                  <CopyButton name={recipe.title} />
                )}
                {!recipe.isSAP && <EditButton recipe={recipe} />}
                {(recipe.user !== null || recipe.isSAP) && (
                  <DeleteButton
                    circle={false}
                    modalTitle={t('recipe.deleteConfirmTitle')}
                    onConfirmDelete={handleConfirmDelete}
                  />
                )}
                <AuthGuard roles={[roles.creator]}>
                  {recipe.user !== null && <MailButton recipe={recipe} />}
                </AuthGuard>

                <AuthGuard roles={[roles.transferAgent]}>
                  {recipe?.user?._id === data?.loggedInUser?.id && (
                    <TransferRecipeModal recipeId={recipe._id} />
                  )}
                </AuthGuard>
                <PrintButton recipe={recipe} />
              </Space>
            ) : (
              <UserRequiredInfo cursor="pointer">
                <Space wrap>
                  <Button icon={<EditOutlined />}>
                    {t('recipe.editButton')}
                  </Button>{' '}
                  <Button icon={<PrinterOutlined />}>
                    {t('recipe.printButton')}
                  </Button>
                </Space>
              </UserRequiredInfo>
            )
          }
        </AuthGuard>
      </ActionCol>
    </Row>
  );
};
